import {
  AfterViewChecked,
  Component,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { PoCodeEditorComponent } from "@po-ui/ng-code-editor";
import {
  PoDialogService,
  PoInputComponent,
  PoModalAction,
  PoModalComponent,
  PoNotificationService,
  PoPageAction,
  PoSelectOption,
  PoTableAction,
  PoTableColumn,
} from "@po-ui/ng-components";
import { Subscription } from "rxjs";

import { MainService } from "../../app.service";
import {
  API_KEY_STATUS_LABELS,
  ApiKeysCsvService,
} from "./api-keys-csv.service";
import {
  ApiKey,
  ApiKeyCanCreateReason,
  ApiKeyCreated,
  ApiKeyDelegate,
  ApiKeyDelegateEntry,
  ApiKeyListResponse,
  ApiKeyUsageExample,
} from "./api-keys.models";
import { ApiKeysService, normalizeUsername } from "./api-keys.service";

@Component({
  selector: "app-api-keys",
  templateUrl: "./api-keys.component.html",
  styleUrls: ["./api-keys.component.css"],
})
export class ApiKeysComponent implements OnInit, AfterViewChecked, OnDestroy {
  private static readonly EXAMPLE_HOST = "esp.example.com";
  private static readonly EXAMPLE_PATH = "/api/v2/recurso";
  private static readonly EXAMPLE_MCP_URL = "https://mcp.example.com/mcp";

  private static readonly CLIENT_ID_PLACEHOLDER = "cole-aqui-o-client-id";
  private static readonly CLIENT_SECRET_PLACEHOLDER =
    "cole-aqui-o-client-secret";

  static readonly DEFAULT_ACTIVE_LIMIT = 10;

  @ViewChildren(PoCodeEditorComponent)
  private codeEditors: QueryList<PoCodeEditorComponent>;

  @ViewChild("apiKeyNameInput") private apiKeyNameInput?: PoInputComponent;

  @ViewChild("createModal", { static: true }) createModal: PoModalComponent;
  @ViewChild("revealModal", { static: true }) revealModal: PoModalComponent;
  @ViewChild("accessModal", { static: true }) accessModal: PoModalComponent;
  @ViewChild("revokeModal", { static: true }) revokeModal: PoModalComponent;

  items: Array<ApiKey> = [];

  canCreate = false;

  canCreateReason: ApiKeyCanCreateReason = null;

  activeLimit = ApiKeysComponent.DEFAULT_ACTIVE_LIMIT;

  limitReachedOwners: Array<string> = [];

  createTargets: Array<string> = [];

  canManageAccess = false;

  manages: Array<string> = [];

  loadingList = false;
  loadingCreate = false;
  loadingRevoke = false;
  loadingAccess = false;

  errorMessage = "";
  newKeyName = "";

  revokeTarget: ApiKey | null = null;

  revokeConfirmation = "";

  newKeyOwner = "";

  accessHolders: Array<ApiKeyDelegate> = [];

  accessUsername = "";

  private readonly subscriptions = new Subscription();

  private readonly minimapHidden = new WeakSet<object>();

  createdClientId: string | null = null;

  createdOwner: string | null = null;

  createdClientSecret: string | null = null;

  copiedClientId = false;
  copiedClientSecret = false;

  readonly columns: Array<PoTableColumn> = [
    { property: "name", label: "Nome", sortable: true },
    { property: "clientId", label: "Client ID", sortable: true },
    {
      property: "maskedClientSecret",
      label: "Final do Client Secret",
      sortable: true,
    },
    {
      property: "status",
      label: "Situação",
      type: "label",
      sortable: true,
      labels: [
        {
          value: "active",
          color: "color-11",
          label: API_KEY_STATUS_LABELS.active,
        },
        {
          value: "revoked",
          color: "color-07",
          label: API_KEY_STATUS_LABELS.revoked,
        },
      ],
    },
    { property: "owner", label: "Conta de serviço", sortable: true },
    {
      property: "createdBy",
      label: "Criada por",
      sortable: true,
    },
    {
      property: "createdAt",
      label: "Criada em",
      type: "dateTime",
      sortable: true,
    },
    {
      property: "revokedBy",
      label: "Revogada por",
      sortable: true,
    },
    {
      property: "revokedAt",
      label: "Revogada em",
      type: "dateTime",
      sortable: true,
    },
  ];

  readonly tableActions: Array<PoTableAction> = [
    {
      label: "Revogar",
      icon: "po-icon po-icon-delete",
      type: "danger",
      action: (row: ApiKey) => this.confirmRevoke(row),
      disabled: (row: ApiKey) => row.status !== "active",
    },
  ];

  readonly createModalPrimaryAction: PoModalAction = {
    label: "Criar",
    disabled: true,
    loading: false,
    action: () => this.create(),
  };

  readonly createModalSecondaryAction: PoModalAction = {
    label: "Cancelar",
    disabled: false,
    action: () => this.closeCreateModal(),
  };

  readonly revealModalPrimaryAction: PoModalAction = {
    label: "Já guardei o Client Secret",
    action: () => this.closeRevealModal(),
  };

  readonly revokeModalPrimaryAction: PoModalAction = {
    label: "Revogar",
    danger: true,
    disabled: true,
    loading: false,
    action: () => this.submitRevoke(),
  };

  readonly revokeModalSecondaryAction: PoModalAction = {
    label: "Cancelar",
    disabled: false,
    action: () => this.closeRevokeModal(),
  };

  readonly accessColumns: Array<PoTableColumn> = [
    { property: "username", label: "Usuário", sortable: true },
    {
      property: "grantedAt",
      label: "Adicionado em",
      type: "dateTime",
      sortable: true,
    },
  ];

  readonly accessActions: Array<PoTableAction> = [
    {
      label: "Remover acesso",
      icon: "po-icon po-icon-delete",
      type: "danger",
      action: (row: ApiKeyDelegate) => this.confirmRevokeAccess(row),
    },
  ];

  readonly pageActions: Array<PoPageAction> = [
    {
      label: "Criar API Key",
      icon: "an an-plus",
      disabled: () =>
        !this.canCreateAny || this.loadingList || this.loadingCreate,
      action: () => this.openCreateModal(),
    },
    {
      label: "Atualizar",
      icon: "an an-arrows-clockwise",
      disabled: () => this.loadingList,
      action: () => this.load(),
    },
    {
      label: "Gerenciar acessos",
      icon: "po-icon po-icon-users",
      visible: () => this.canManageAccess,
      disabled: () => this.loadingAccess,
      action: () => this.openAccessModal(),
    },
    {
      label: "Exportar CSV",
      icon: "an an-file-csv",
      disabled: () => this.loadingList || !this.items.length,
      action: () => this.exportCsv(),
    },
  ];

  private static readonly TABLE_LITERALS_OWNER = {
    noData: "Nenhuma API Key criada.",
  };

  private static readonly TABLE_LITERALS_READER = {
    noData: "Nenhuma API Key compartilhada com você.",
  };

  get tableLiterals(): { noData: string } {
    return this.canCreateAny
      ? ApiKeysComponent.TABLE_LITERALS_OWNER
      : ApiKeysComponent.TABLE_LITERALS_READER;
  }

  usageExamples: Array<ApiKeyUsageExample> = [];

  copiedExampleId: string | null = null;

  constructor(
    private apiKeysService: ApiKeysService,
    private apiKeysCsvService: ApiKeysCsvService,
    private notification: PoNotificationService,
    private dialog: PoDialogService,
    private mainService: MainService,
  ) {}

  ngOnInit(): void {
    this.usageExamples = this.buildUsageExamples();
    this.load();
  }

  ngAfterViewChecked(): void {
    this.hideEditorMinimaps();
  }

  ngOnDestroy(): void {
    this.clearRevealedSecret();
    this.subscriptions.unsubscribe();
  }

  private hideEditorMinimaps(): void {
    this.codeEditors?.forEach((component) => {
      const editor = component.editor;

      if (
        !editor ||
        typeof editor.updateOptions !== "function" ||
        this.minimapHidden.has(editor)
      ) {
        return;
      }

      this.minimapHidden.add(editor);
      editor.updateOptions({ minimap: { enabled: false } });
    });
  }

  load(): void {
    this.loadingList = true;
    this.errorMessage = "";

    this.apiKeysService.list().subscribe({
      next: (response) => {
        this.items = response.items || [];
        this.canCreate = response.canCreate === true;
        this.canCreateReason = response.canCreateReason || null;
        this.activeLimit = this.resolveActiveLimit(response);
        this.manages = response.manages || [];
        this.createTargets = this.resolveCreateTargets(response);
        this.canManageAccess = this.resolveCanManageAccess(response);
        this.limitReachedOwners = this.resolveLimitReachedOwners(response);
        this.loadingList = false;
      },
      error: (error: Error) => {
        this.items = [];
        this.canCreate = false;
        this.canCreateReason = null;
        this.activeLimit = ApiKeysComponent.DEFAULT_ACTIVE_LIMIT;
        this.limitReachedOwners = [];
        this.manages = [];
        this.createTargets = [];
        this.canManageAccess = false;
        this.errorMessage = error.message;
        this.loadingList = false;
      },
    });
  }

  private resolveCreateTargets(response: ApiKeyListResponse): Array<string> {
    if (Array.isArray(response.createTargets)) {
      return response.createTargets.filter((owner) => !!owner);
    }

    return response.canCreate === true && this.currentUsername
      ? [this.currentUsername]
      : [];
  }

  private resolveActiveLimit(response: ApiKeyListResponse): number {
    const reported = response.activeLimit;

    if (
      typeof reported === "number" &&
      Number.isInteger(reported) &&
      reported > 0
    ) {
      return reported;
    }

    if (this.canCreateReason === "active_limit_reached") {
      const observed = this.countOwnActiveKeys();
      if (observed > 0) {
        return observed;
      }
    }

    return ApiKeysComponent.DEFAULT_ACTIVE_LIMIT;
  }

  private countOwnActiveKeys(): number {
    const own = normalizeUsername(this.currentUsername);

    return this.items.filter(
      (item) => item.status === "active" && (!item.owner || item.owner === own),
    ).length;
  }

  private resolveLimitReachedOwners(
    response: ApiKeyListResponse,
  ): Array<string> {
    if (Array.isArray(response.limitReachedOwners)) {
      return response.limitReachedOwners.filter((owner) => !!owner);
    }

    return this.deriveLimitReachedOwners();
  }

  private deriveLimitReachedOwners(): Array<string> {
    const offered = new Set(this.createTargets);
    const own = normalizeUsername(this.currentUsername);

    const activeByOwner = new Map<string, number>();
    this.items.forEach((item) => {
      if (item.status !== "active" || !item.owner) {
        return;
      }

      activeByOwner.set(item.owner, (activeByOwner.get(item.owner) || 0) + 1);
    });

    return this.manages.filter(
      (owner) =>
        !!owner &&
        owner !== own &&
        !offered.has(owner) &&
        (activeByOwner.get(owner) || 0) >= this.activeLimit,
    );
  }

  private resolveCanManageAccess(response: ApiKeyListResponse): boolean {
    return typeof response.canManageAccess === "boolean"
      ? response.canManageAccess
      : response.canCreate === true;
  }

  get canCreateAny(): boolean {
    return this.createTargets.length > 0;
  }

  get createOwnerOptions(): Array<PoSelectOption> {
    return this.createTargets.map((owner) => ({ label: owner, value: owner }));
  }

  get showOwnerPicker(): boolean {
    return this.createTargets.length > 1;
  }

  openCreateModal(): void {
    this.newKeyName = "";
    this.newKeyOwner = this.resolveNewKeyOwner();
    this.syncCreateAction();
    this.createModal.open();
    this.focusCreateName();
  }

  private focusCreateName(): void {
    if (this.showOwnerPicker) {
      return;
    }

    setTimeout(() => this.apiKeyNameInput?.focus());
  }

  private resolveNewKeyOwner(): string {
    const chosen = (this.newKeyOwner || "").trim();
    if (chosen) {
      return chosen;
    }

    return this.createTargets.length === 1 ? this.createTargets[0] : "";
  }

  onCreateModalClose(): void {
    this.newKeyName = "";
    this.newKeyOwner = "";
    this.syncCreateAction();
  }

  closeCreateModal(): void {
    this.onCreateModalClose();
    this.createModal.close();
  }

  create(): void {
    const name = (this.newKeyName || "").trim();
    if (!name) {
      return;
    }

    const owner = this.resolveNewKeyOwner();
    if (!owner) {
      return;
    }

    if (this.loadingCreate) {
      return;
    }

    this.setCreateLoading(true);

    this.subscriptions.add(
      this.apiKeysService.create(name, owner).subscribe({
        next: (created: ApiKeyCreated) => {
          this.setCreateLoading(false);
          this.createModal.close();
          this.newKeyName = "";

          this.createdClientId = created.clientId;
          this.createdClientSecret = created.clientSecret;
          this.createdOwner = created.owner || owner;
          this.newKeyOwner = "";
          this.copiedClientId = false;
          this.copiedClientSecret = false;
          this.revealModal.open();

          this.load();
        },
        error: (error: Error) => {
          this.setCreateLoading(false);
          this.notification.error(error.message);
        },
      }),
    );
  }

  private setCreateLoading(loading: boolean): void {
    this.loadingCreate = loading;
    this.syncCreateAction();
  }

  get createReady(): boolean {
    return !!this.newKeyName.trim() && !!this.resolveNewKeyOwner();
  }

  private syncCreateAction(): void {
    this.createModalPrimaryAction.disabled =
      !this.createReady || this.loadingCreate;
    this.createModalPrimaryAction.loading = this.loadingCreate;
    this.createModalSecondaryAction.disabled = this.loadingCreate;
  }

  onNewKeyNameChange(value: string): void {
    this.newKeyName = value || "";
    this.syncCreateAction();
  }

  onNewKeyOwnerChange(value: string): void {
    this.newKeyOwner = value || "";
    this.syncCreateAction();
  }

  onRevealModalClose(): void {
    this.clearRevealedSecret();
  }

  closeRevealModal(): void {
    this.clearRevealedSecret();
    this.revealModal.close();
  }

  clearRevealedSecret(): void {
    this.createdClientSecret = "";
    this.createdClientSecret = null;
    this.copiedClientId = false;
    this.copiedClientSecret = false;
  }

  confirmRevoke(key: ApiKey): void {
    this.revokeTarget = key;
    this.revokeConfirmation = "";
    this.syncRevokeAction();
    this.revokeModal.open();
  }

  get revokeConfirmed(): boolean {
    const target = (this.revokeTarget?.name || "").trim();

    return target.length > 0 && this.revokeConfirmation.trim() === target;
  }

  onRevokeConfirmationChange(value: string): void {
    this.revokeConfirmation = value || "";
    this.syncRevokeAction();
  }

  get revokeConfirmationStarted(): boolean {
    return this.revokeConfirmation.trim().length > 0;
  }

  get revokeConfirmationIcon(): string {
    return this.revokeConfirmed
      ? "an an-check"
      : "an an-key";
  }

  private syncRevokeAction(): void {
    this.revokeModalPrimaryAction.disabled =
      !this.revokeConfirmed || this.loadingRevoke;
    this.revokeModalPrimaryAction.loading = this.loadingRevoke;
    this.revokeModalSecondaryAction.disabled = this.loadingRevoke;
  }

  onRevokeModalClose(): void {
    this.revokeTarget = null;
    this.revokeConfirmation = "";
    this.syncRevokeAction();
  }

  closeRevokeModal(): void {
    this.onRevokeModalClose();
    this.revokeModal.close();
  }

  submitRevoke(): void {
    if (!this.revokeConfirmed || this.loadingRevoke || !this.revokeTarget) {
      return;
    }

    this.revoke(this.revokeTarget);
  }

  revoke(key: ApiKey): void {
    this.loadingRevoke = true;
    this.syncRevokeAction();

    this.apiKeysService.revoke(key).subscribe({
      next: () => {
        this.loadingRevoke = false;
        this.closeRevokeModal();
        this.notification.success("API Key revogada.");
        this.load();
      },
      error: (error: Error) => {
        this.loadingRevoke = false;
        this.syncRevokeAction();
        this.notification.error(error.message);
      },
    });
  }

  exportCsv(): void {
    if (!this.items.length) {
      this.notification.warning("Não há API Keys para exportar.");
      return;
    }

    this.runExport(() => this.apiKeysCsvService.exportApiKeys(this.items));
  }

  private runExport(exportFile: () => string): void {
    let filename: string;

    try {
      filename = exportFile();
    } catch {
      this.notification.error(
        "Não foi possível gerar o arquivo. Verifique se o navegador está bloqueando downloads.",
      );
      return;
    }

    this.notification.success(`Arquivo ${filename} gerado.`);
  }

  get currentUsername(): string {
    return this.mainService.userToken.username || "";
  }

  get hasReachedActiveLimit(): boolean {
    return (
      this.canCreateReason === "active_limit_reached" &&
      !this.loadingList &&
      !this.errorMessage
    );
  }

  get hasLimitReachedOwners(): boolean {
    return (
      this.limitReachedOwners.length > 0 &&
      !this.loadingList &&
      !this.errorMessage
    );
  }

  get showActiveLimitWarning(): boolean {
    return this.hasReachedActiveLimit || this.hasLimitReachedOwners;
  }

  get limitReachedOwnersMessage(): string {
    const accounts = this.formatAccountList(this.limitReachedOwners);

    if (this.limitReachedOwners.length === 1) {
      return (
        `A conta ${accounts} atingiu o limite de ${this.activeLimitLabel}. ` +
        "Revogue uma chave dessa conta que não esteja mais em uso para criar outra."
      );
    }

    return (
      `As contas ${accounts} atingiram o limite de ${this.activeLimitLabel}. ` +
      "Revogue uma chave que não esteja mais em uso na conta em que você precisa criar."
    );
  }

  private get activeLimitLabel(): string {
    return this.activeLimit === 1
      ? "1 API Key ativa"
      : `${this.activeLimit} API Keys ativas`;
  }

  private formatAccountList(accounts: Array<string>): string {
    if (accounts.length <= 1) {
      return accounts[0] || "";
    }

    return `${accounts.slice(0, -1).join(", ")} e ${
      accounts[accounts.length - 1]
    }`;
  }

  get managesOnlySharedKeys(): boolean {
    return this.canCreateReason === "no_capability" && !this.canManageAccess;
  }

  get canCreateMessage(): string {
    switch (this.canCreateReason) {
      case "no_capability":
        return (
          "Sua conta não tem permissão para criar API Keys próprias. " +
          "As API Keys compartilhadas com você aparecem na lista abaixo. " +
          "Peça a liberação ao time responsável pelo ESP se precisar de uma chave própria."
        );
      case "active_limit_reached":
        return `Você atingiu o limite de ${this.activeLimitLabel}. Revogue uma chave que não esteja mais em uso para criar outra.`;
      case "unknown_account":
        return "Não encontramos sua conta no ESP. Fale com o time responsável.";
      default:
        return `Para criar uma API Key é preciso ter a permissão de API Keys no ESP e estar dentro do limite de ${this.activeLimitLabel}.`;
    }
  }

  openAccessModal(): void {
    this.accessUsername = "";
    this.loadDelegates();
    this.accessModal.open();
  }

  onAccessModalClose(): void {
    this.accessUsername = "";
  }

  closeAccessModal(): void {
    this.onAccessModalClose();
    this.accessModal.close();

    this.load();
  }

  private toAccessHolders(
    delegates: Array<ApiKeyDelegateEntry> | undefined,
  ): Array<ApiKeyDelegate> {
    return (delegates || [])
      .map((entry) =>
        typeof entry === "string" ? { username: entry } : { ...entry },
      )
      .filter((holder) => !!holder.username)
      .map((holder) =>
        holder.grantedAt
          ? { username: holder.username, grantedAt: holder.grantedAt }
          : { username: holder.username },
      );
  }

  private setAccessHolders(
    delegates: Array<ApiKeyDelegateEntry> | undefined,
  ): void {
    this.accessHolders = this.toAccessHolders(delegates);
  }

  private loadDelegates(): void {
    this.loadingAccess = true;

    this.subscriptions.add(
      this.apiKeysService.listDelegates().subscribe({
        next: (response) => {
          this.setAccessHolders(response.delegates);
          this.loadingAccess = false;
        },
        error: (error: Error) => {
          this.setAccessHolders([]);
          this.loadingAccess = false;
          this.notification.error(error.message);
        },
      }),
    );
  }

  grantAccess(): void {
    const typed = (this.accessUsername || "").trim();
    if (!typed) {
      return;
    }

    const username = normalizeUsername(typed);
    if (!username) {
      this.notification.warning("Informe um usuário ou e-mail.");
      return;
    }

    if (username === this.currentUsername.toLowerCase()) {
      this.notification.warning("Você já gerencia as suas próprias API Keys.");
      return;
    }

    if (this.accessHolders.some((holder) => holder.username === username)) {
      this.notification.warning(`${username} já tem acesso.`);
      this.accessUsername = "";
      return;
    }

    this.loadingAccess = true;

    this.subscriptions.add(
      this.apiKeysService.addDelegate(username).subscribe({
        next: (response) => {
          this.setAccessHolders(response.delegates);
          this.accessUsername = "";
          this.loadingAccess = false;
          this.notification.success(`Acesso concedido a ${username}.`);
        },
        error: (error: Error) => {
          this.loadingAccess = false;
          this.notification.error(error.message);
        },
      }),
    );
  }

  confirmRevokeAccess(holder: ApiKeyDelegate): void {
    this.dialog.confirm({
      title: "Remover acesso",
      message:
        `<strong>${holder.username}</strong> deixa de ver, criar e revogar as suas API Keys. ` +
        "As chaves em si continuam funcionando: só o acesso desta pessoa é removido. Deseja continuar?",
      confirm: () => this.revokeAccess(holder),
    });
  }

  revokeAccess(holder: ApiKeyDelegate): void {
    this.loadingAccess = true;

    this.subscriptions.add(
      this.apiKeysService.removeDelegate(holder.username).subscribe({
        next: (response) => {
          this.setAccessHolders(response.delegates);
          this.loadingAccess = false;
          this.notification.success(`Acesso de ${holder.username} removido.`);
        },
        error: (error: Error) => {
          this.loadingAccess = false;
          this.notification.error(error.message);
          this.loadDelegates();
        },
      }),
    );
  }

  exportAccessCsv(): void {
    if (!this.accessHolders.length) {
      this.notification.warning("Não há acessos para exportar.");
      return;
    }

    this.runExport(() =>
      this.apiKeysCsvService.exportDelegates(
        this.accessHolders,
        this.currentUsername,
      ),
    );
  }

  private buildUsageExamples(): Array<ApiKeyUsageExample> {
    const host = ApiKeysComponent.EXAMPLE_HOST;
    const path = ApiKeysComponent.EXAMPLE_PATH;
    const resourceUrl = `https://${host}${path}`;
    const clientId = ApiKeysComponent.CLIENT_ID_PLACEHOLDER;
    const clientSecret = ApiKeysComponent.CLIENT_SECRET_PLACEHOLDER;

    return [
      {
        id: "mcp",
        label: "MCP",
        language: "json",
        hint: "Para conectar um servidor MCP. Cole o par no arquivo de configuração do seu cliente, dentro de mcpServers.",
        code: [
          "{",
          '  "mcpServers": {',
          '    "mcp-example": {',
          '      "type": "http",',
          `      "url": "${ApiKeysComponent.EXAMPLE_MCP_URL}",`,
          '      "headers": {',
          `        "X-Client-Id": "${clientId}",`,
          `        "X-Client-Secret": "${clientSecret}"`,
          "      }",
          "    }",
          "  }",
          "}",
        ].join("\n"),
      },
      {
        id: "curl",
        label: "cURL",
        language: "shell",
        hint: "Para testar a chave pelo terminal e confirmar que ela funciona antes de colocar numa integração.",
        code: [
          "curl --silent --show-error --fail \\",
          `  --header "X-Client-Id: ${clientId}" \\`,
          `  --header "X-Client-Secret: ${clientSecret}" \\`,
          '  --header "Accept: application/json" \\',
          `  "${resourceUrl}"`,
        ].join("\n"),
      },
      {
        id: "http",
        label: "HTTP",
        language: "plaintext",
        hint: "A requisição inteira, como o ESP a recebe. Útil para conferir o que está sendo enviado quando a chamada não funciona.",
        code: [
          `GET ${path} HTTP/1.1`,
          `Host: ${host}`,
          "Accept: application/json",
          `X-Client-Id: ${clientId}`,
          `X-Client-Secret: ${clientSecret}`,
        ].join("\n"),
      },
      {
        id: "javascript",
        label: "JavaScript",
        language: "javascript",
        hint: "Para uma integração em Node.js. Se a resposta vier 401, a chave está inválida ou foi revogada; 403 significa que a Conta de serviço não tem acesso àquele recurso.",
        code: [
          `const response = await fetch('${resourceUrl}', {`,
          "  headers: {",
          `    'X-Client-Id': '${clientId}',`,
          `    'X-Client-Secret': '${clientSecret}',`,
          "    Accept: 'application/json'",
          "  }",
          "});",
          "",
          "if (!response.ok) {",
          "  throw new Error('ESP respondeu ' + response.status);",
          "}",
          "",
          "const dados = await response.json();",
        ].join("\n"),
      },
      {
        id: "python",
        label: "Python",
        language: "python",
        hint: "Para um script ou serviço em Python, usando a biblioteca requests.",
        code: [
          "import requests",
          "",
          "response = requests.get(",
          `    "${resourceUrl}",`,
          "    headers={",
          `        "X-Client-Id": "${clientId}",`,
          `        "X-Client-Secret": "${clientSecret}",`,
          '        "Accept": "application/json",',
          "    },",
          "    timeout=30,",
          ")",
          "response.raise_for_status()",
          "",
          "dados = response.json()",
        ].join("\n"),
      },
    ];
  }

  copyExample(example: ApiKeyUsageExample): void {
    this.copy(example.code, `Exemplo ${example.label} copiado.`, () => {
      this.copiedExampleId = example.id;
    });
  }

  copyClientId(): void {
    if (!this.createdClientId) {
      return;
    }
    this.copy(this.createdClientId, "Client ID copiado.", () => {
      this.copiedClientId = true;
    });
  }

  copyClientSecret(): void {
    if (!this.createdClientSecret) {
      return;
    }
    this.copy(this.createdClientSecret, "Client Secret copiado.", () => {
      this.copiedClientSecret = true;
    });
  }

  private copy(
    value: string,
    successMessage: string,
    onCopied?: () => void,
  ): void {
    const clipboard =
      typeof navigator !== "undefined" ? navigator.clipboard : undefined;

    if (!clipboard || typeof clipboard.writeText !== "function") {
      this.notification.warning(
        "O navegador não permitiu copiar. Selecione o valor manualmente.",
      );
      return;
    }

    try {
      Promise.resolve(clipboard.writeText(value)).then(
        () => {
          onCopied?.();
          this.notification.success(successMessage);
        },
        () =>
          this.notification.warning(
            "Não foi possível copiar. Selecione o valor manualmente.",
          ),
      );
    } catch {
      this.notification.warning(
        "Não foi possível copiar. Selecione o valor manualmente.",
      );
    }
  }
}
